import { CourseType, ResidencyStatus } from '@gpa-scales/shared-types';
import type { AppStore } from '../useAppStore';
import { detectEdgeCases } from '@gpa-scales/shared-logic/engine/edgeCaseDetection';
import { ucCappedGPA } from '@gpa-scales/shared-logic/engine/gpaCalculations';

export interface NonResidentHonorsPenalty {
  penalty: number;
  flaggedCount: number;
}

/**
 * FE-E6 — Quantify how much UC Capped GPA a non-resident loses because their
 * out-of-state honors / Dual Enrollment courses don't earn UC bonus points.
 *
 * Computed as the difference between the actual UC Capped GPA and a synthetic
 * "as if California resident" GPA where each flagged course is treated as an
 * AP course (AP is always honors-eligible). The store's real `result` is never
 * mutated — this selector recomputes on copies.
 *
 * Returns `null` when the student is not a non-resident, or when no honors
 * courses are flagged for the out-of-state rule.
 */
export function selectNonResidentHonorsPenalty(
  state: AppStore,
): NonResidentHonorsPenalty | null {
  if (state.settings.residencyStatus !== ResidencyStatus.NonResident) {
    return null;
  }

  const cases = detectEdgeCases(state.courses, state.settings).filter(
    (c) => c.type === 'out_of_state_honors',
  );
  if (cases.length === 0) return null;

  const flaggedIds = new Set(cases.map((c) => c.courseId));

  // Recompute on a copy — never touch the real result.
  const asIfCourses = state.courses.map((c) =>
    flaggedIds.has(c.id) ? { ...c, courseType: CourseType.AP } : c,
  );

  const actual = ucCappedGPA(state.courses, state.settings).value;
  const asIf = ucCappedGPA(asIfCourses, state.settings).value;

  return {
    penalty: Math.max(0, asIf - actual),
    flaggedCount: cases.length,
  };
}
